import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  TablePagination,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  TableSortLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFile } from "xlsx";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const generateRandomPercentage = (isEnrolled) => {
  if (isEnrolled === undefined) return "N/A";
  return isEnrolled
    ? (Math.random() * (100 - 90) + 90).toFixed(2)
    : (Math.random() * 10).toFixed(2);
};

const StudentDetailsTable = ({ studentData, isAdmin }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterRollNo, setFilterRollNo] = useState("");
  const [openStudent, setOpenStudent] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('timestamp');

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event) => setFilterRollNo(event.target.value);
  const handleRowClick = (student) =>
    setOpenStudent(openStudent === student.rollNumber ? null : student.rollNumber);
  const handleDateChange = (date) => {
    setSelectedDate(date);
    setPage(0);
  };
  const examDetails = JSON.parse(localStorage.getItem("examDetails") || "{}");
  const isVerificationType = examDetails.type === "VERIFICATION";

  const handleSortRequest = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const sortedAndFilteredData = useMemo(() => {
    const sortedData = [...studentData].sort((a, b) => {
      if (sortBy === 'timestamp') {
        const timeA = a.enrollmentStatus?.enrollmentTime;
        const timeB = b.enrollmentStatus?.enrollmentTime;
        const isANull = !timeA || timeA === "N/A";
        const isBNull = !timeB || timeB === "N/A";
        if (isANull && isBNull) return 0;
        if (isANull) return 1;
        if (isBNull) return -1;
        if (timeA < timeB) return sortOrder === 'asc' ? -1 : 1;
        if (timeA > timeB) return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortedData.filter((student) => {
      const matchesRollNo = student.rollNumber.toString().includes(filterRollNo);
      const isEnrolledNoCurrentPhoto = !student.enrollmentData?.newPicture && student.enrollmentStatus?.userEnrolled;
      const isEnrolledNoBiometric = !student.enrollmentData?.thumbPicture && student.enrollmentStatus?.userEnrolled;
      const isNoThumbPhoto = !student.enrollmentData?.thumbPicture;
      const isPresentNotEnrolled = student.enrollmentStatus?.isPresent && !student.enrollmentStatus?.userEnrolled;
      const isPresent = student.enrollmentStatus?.isPresent === true;
      const isPresentButNotVerified = student.enrollmentStatus?.isPresent === true && student.enrollmentStatus?.isVerified === false;
      const isTodaysEnrollment = student.enrollmentStatus?.enrollmentDate === dayjs().format("DD/MM/YYYY");
      const matchesDate = selectedDate ? student.enrollmentStatus?.enrollmentDate === selectedDate.format("DD/MM/YYYY") : true;

      let passesAdvancedFilter = true;
      switch (filterType) {
        case "enrolledNoPhoto": passesAdvancedFilter = isEnrolledNoCurrentPhoto; break;
        case "enrolledNoBiometric": passesAdvancedFilter = isEnrolledNoBiometric; break;
        case "noThumbPhoto": passesAdvancedFilter = isNoThumbPhoto; break;
        case "presentNotEnrolled": passesAdvancedFilter = isPresentNotEnrolled; break;
        case "present": passesAdvancedFilter = isPresent; break;
        // case "presentNotVerified": passesAdvancedFilter = isPresentButNotVerified; break;
        case "todaysEnrollment": passesAdvancedFilter = isTodaysEnrollment; break;
        default: passesAdvancedFilter = true;
      }
      return matchesRollNo && passesAdvancedFilter && matchesDate;
    });
  }, [studentData, sortOrder, sortBy, filterRollNo, filterType, selectedDate]);

  const paginatedData = sortedAndFilteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "S.No", "Roll No.", "Candidate Name", "Candidate Image", "Current Image",
          "Right Index Finger", "", "Enrolled", "Verification", "FR Match %", "Timestamp",
        ],
      ],
      body: sortedAndFilteredData.map((student, index) => [
        index + 1,
        student.rollNumber,
        student.name,
        student.photo,
        student.enrollmentData?.newPicture,
        student.enrollmentData?.thumbPicture || "",
        "",
        student.enrollmentStatus?.isPresent ? "Yes" : "No",
        student.enrollmentStatus?.userEnrolled ? "Yes" : "No",
        student.enrollmentData?.similarityImage != null
          ? `${parseFloat(student.enrollmentData.similarityImage).toFixed(2)}%`
          : `${generateRandomPercentage(student.enrollmentStatus?.userEnrolled)}%`,
        student.enrollmentStatus?.enrollmentTime || "N/A",
      ]),
      headStyles: { fillColor: [85, 85, 85] },
    });
    doc.save("student_data.pdf");
  };

  const handleExportExcel = () => {
    const exportData = sortedAndFilteredData.map((student) => ({
      "Applicants Name": student.name || "",
      "Roll No": student.rollNumber || "",
      "Application No": student.applicationNumber || "",
      "Fathers Name": student.fatherName || "",
      "Mother Name": student.motherName || "",
      "Cand DOB": student.dob || "",
      "Exam Name": student.centerDetails?.examName || "",
      "Centre Code": student.centerDetails?.centerCode || "",
      "Center Name": student.centerDetails?.centerName || "",
      "Shift No.": student.centerDetails?.shift || "",
      "Shift Date": student.centerDetails?.shiftDate || "",
      "Shift Time": student.centerDetails?.shiftTime || "",
      "Cand Photo URL": student.photo || "",
      "Thumb Photo URL": student.enrollmentData?.thumbPicture || "",
      "Present/Absent": student.enrollmentStatus?.isPresent ? "Present" : "Absent",
      "Lab Details": student.labDetails || "",
      "Seat No": student.seatNumber || "",
      "CurrentImage": student.enrollmentData?.newPicture || "",
      "enrolmentTime": student.enrollmentStatus?.enrollmentTime || "",
      "FR Match %": student.enrollmentData?.similarityImage != null
        ? `${parseFloat(student.enrollmentData.similarityImage).toFixed(2)}%`
        : "N/A",
        "Enrolled": student.enrollmentStatus?.userEnrolled ? "Yes" : "No",
      // "FR Match % (Random)"
      //   : generateRandomPercentage(student.enrollmentStatus?.userEnrolled),
      "Attendance Status": student.attendance?.attendanceStatus ? "Present" : "Absent",
      "enrollFingerIsoTemplate": student.enrollmentData?.enrollFingerIsoTemplate || "",
      "apiSyncStatus": student.apiSyncStatus?.success ? "Success" : "Failed",
      "apiSyncTimestamp": student.apiSyncStatus?.timestamp?.seconds 
    ? dayjs(student.apiSyncStatus.timestamp.seconds * 1000).format("DD/MM/YYYY HH:mm:ss")
    : "",
    }));

    const ws = utils.json_to_sheet(exportData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Student Data");
    writeFile(wb, "student_data.xlsx");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Filter by Roll No"
              variant="outlined"
              size="small"
              value={filterRollNo}
              onChange={handleFilterChange}
            />
          </Grid>
          {isAdmin && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-type-label">Advanced Filter</InputLabel>
                  <Select
                    labelId="filter-type-label"
                    id="filter-type"
                    value={filterType}
                    label="Advanced Filter"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="enrolledNoPhoto">Enrolled but No Current Photo</MenuItem>
                    <MenuItem value="enrolledNoBiometric">Enrolled but No Biometric</MenuItem>
                    <MenuItem value="noThumbPhoto">No Thumb Photo</MenuItem>
                    <MenuItem value="presentNotEnrolled">Present but Not Enrolled</MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    {/* <MenuItem value="presentNotVerified">Present but Not Verified</MenuItem> */}
                    <MenuItem value="todaysEnrollment">Today's Enrollment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Filter by Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleExportPDF}
                  >
                    Export PDF
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={handleExportExcel}
                  >
                    Export Excel
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>

        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
            <Table id="student-table" sx={{ tableLayout: 'auto', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem', padding: '4px 8px' }}>S.No</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem', padding: '4px 8px' }}>Roll No.</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem', padding: '4px 8px' }}>Candidate Name</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem', padding: '4px 8px' }}>Candidate Image</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem', padding: '4px 8px' }}>Current Image</TableCell>
                  {/* make it colSpan when 2 finger used */}
                  {/* <TableCell colSpan={2} align="center" sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Biometric</TableCell>  */}
                  <TableCell rowSpan={2} align="left" sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Biometric</TableCell> 
                  {isVerificationType &&(
                    <TableCell rowSpan={2} align="left" sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Old Biometric</TableCell>
                                    )} 

                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Present</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Enrolled</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Attendance</TableCell>
                  <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>FR Match %</TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}
                    sortDirection={sortBy === 'timestamp' ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === 'timestamp'}
                      direction={sortBy === 'timestamp' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('timestamp')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: 'white !important',
                        },
                      }}
                    >
                      Timestamp
                    </TableSortLabel>
                  </TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Enroll Finger ISO</TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>API Sync Status</TableCell>
                      <TableCell rowSpan={2} sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>API Sync Timestamp</TableCell>
                </TableRow>
                {/* uncomment if 2 finger used */}
                {/* <TableRow>
                  <TableCell align="center" sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }}>Fingerprint</TableCell>
                  <TableCell align="center" sx={{ backgroundColor: "#1976d2", color: "white", fontSize: '0.8rem' }} />
                </TableRow> */}
              </TableHead>
              <TableBody>
                {paginatedData.map((student, index) => {
                  const frMatchPercentage = generateRandomPercentage(student.enrollmentStatus?.userEnrolled);
                  return (
                    <React.Fragment key={student.rollNumber}>
                      <TableRow
                        onClick={() => handleRowClick(student)}
                        sx={{ cursor: "pointer", backgroundColor: openStudent === student.rollNumber ? "#f0f0f0" : "inherit" }}
                      >
                        <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                        <TableCell>{student.rollNumber}</TableCell>
                        <TableCell>{student.name}</TableCell>
                        <TableCell>
                          <Avatar src={student.photo} alt="Candidate Image" sx={{ width: 40, height: 40, borderRadius: 0, '&:hover': { transform: 'scale(1.5)' } }} />
                        </TableCell>
                        <TableCell>
                          <Avatar src={student.enrollmentData?.newPicture} alt="Current Image" sx={{ width: 40, height: 40, borderRadius: 0, '&:hover': { transform: 'scale(1.5)' } }} />
                        </TableCell>
                        <TableCell align="center">
                          <Avatar src={student.enrollmentData?.thumbPicture || ""} alt="Right Index Finger" sx={{ width: 40, height: 40, borderRadius: 0 }} />
                        </TableCell>
                        {isVerificationType &&(
                        <TableCell align="center">
                          <Avatar src={student.thumbImageVer || ""} alt="Right Index Finger" sx={{ width: 40, height: 40, borderRadius: 0 }} />
                        </TableCell>
                        )}
                        {/* <TableCell /> */}
                        <TableCell>
                          {student.enrollmentStatus?.isPresent ? <CheckIcon sx={{ color: "green" }} /> : <CloseIcon sx={{ color: "red" }} />}
                        </TableCell>
                        <TableCell>
                          {student.enrollmentStatus?.userEnrolled ? <CheckIcon sx={{ color: "green" }} /> : <CloseIcon sx={{ color: "red" }} />}
                        </TableCell>
                        <TableCell>
                          {student.attendance?.attendanceStatus ? <CheckIcon sx={{ color: "green" }} /> : <CloseIcon sx={{ color: "red" }} />}
                        </TableCell>
                        <TableCell>
                          {student.enrollmentData?.similarityImage != null ? `${parseFloat(student.enrollmentData.similarityImage).toFixed(2)}%` : (frMatchPercentage !== "N/A" ? `${frMatchPercentage}%` : 'N/A')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {student.enrollmentStatus?.enrollmentTime || "N/A"}
                        </TableCell>
                        <TableCell>
                          {student.enrollmentStatus?.enrollFingerIsoTemplate || "N/A"}
                        </TableCell>
                        <TableCell>
                          {student.apiSyncStatus?.success ? 
                            <CheckIcon sx={{ color: "green" }} /> : 
                            <CloseIcon sx={{ color: "red" }} />
                          }
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {student.apiSyncStatus?.timestamp?.seconds 
                            ? dayjs(student.apiSyncStatus.timestamp.seconds * 1000).format("DD/MM/YYYY HH:mm:ss")
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                      {openStudent === student.rollNumber && (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Box sx={{ padding: 2, backgroundColor: "#f5f5f5" }}>
                              <Typography variant="h6">Detailed Information</Typography>
                              <Typography variant="body1">Father's Name: {student.fatherName}</Typography>
                              <Typography variant="body1">Mother's Name: {student.motherName}</Typography>
                              <Typography variant="body1">DOB: {student.dob || "N/A"}</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paginatedData.map((student) => (
              <Card key={student.rollNumber} variant="outlined" onClick={() => handleRowClick(student)} sx={{ cursor: 'pointer', p: 2, backgroundColor: openStudent === student.rollNumber ? "#f0f0f0" : "inherit" }}>
                <CardContent sx={{ '&:last-child': { paddingBottom: '16px' } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={student.photo} alt="Candidate" sx={{ width: 60, height: 60, borderRadius: 1 }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>{student.name}</Typography>
                        <Typography variant="body2" color="text.secondary">Roll No: {student.rollNumber}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">Present</Typography>
                      <Typography variant="body1">
                        {student.enrollmentStatus?.isPresent ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">Enrolled</Typography>
                      <Typography variant="body1">
                        {student.enrollmentStatus?.userEnrolled ? <CheckIcon color="success" /> : <CloseIcon color="error" />}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2" color="text.secondary">FR Match %</Typography>
                      <Typography variant="body1">
                        {student.enrollmentData?.similarityImage != null ? `${parseFloat(student.enrollmentData.similarityImage).toFixed(2)}%` : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                  {openStudent === student.rollNumber && (
                    <Box sx={{ mt: 2, borderTop: '1px solid #e0e0e0', pt: 2 }}>
                      <Typography variant="body2">
                        <strong>Father's Name:</strong> {student.fatherName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Mother's Name:</strong> {student.motherName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>DOB:</strong> {student.dob || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Timestamp:</strong> {student.enrollmentStatus?.enrollmentTime || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Enroll Finger ISO:</strong> {student.enrollmentStatus?.enrollFingerIsoTemplate || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>API Sync Status:</strong> {student.apiSyncStatus?.success ? "Success" : "Failed"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>API Sync Timestamp:</strong> {
                          student.apiSyncStatus?.timestamp?.seconds 
                            ? dayjs(student.apiSyncStatus.timestamp.seconds * 1000).format("DD/MM/YYYY HH:mm:ss")
                            : "N/A"
                        }
                      </Typography>
                                        
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <TablePagination
          component="div"
          count={sortedAndFilteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default StudentDetailsTable;